import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { AlertCircle, Check, ClipboardCheck, FileText, Info, Lightbulb, Sparkles, X } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import type { ReactNode } from 'react'

const SECTION_HEADINGS = {
  SUMMARY: 'summary',
  'KEY POINTS': 'keyPoints',
  'WHAT YOU NEED TO DO': 'yourResponsibilities',
} as const

type SectionHeading = keyof typeof SECTION_HEADINGS

interface IParsedPolicySummary {
  summary: string
  keyPoints: string[]
  yourResponsibilities: string[]
}

const isHeading = (line: string): line is SectionHeading => line in SECTION_HEADINGS

/** A trailing line still being streamed that may turn into a heading - hidden until it completes. */
const isPartialHeading = (line: string) =>
  !!line && Object.keys(SECTION_HEADINGS).some((heading) => heading.startsWith(line))

/**
 * Parse the AI's plain text summary (SUMMARY / KEY POINTS / WHAT YOU NEED TO DO
 * sections) into structured parts. Safe to call on partially streamed text.
 */
const parsePolicySummary = (text: string, isStreaming: boolean): IParsedPolicySummary => {
  const lines = text.split('\n').map((line) => line.trim())
  const lastLine = lines[lines.length - 1]
  if (isStreaming && isPartialHeading(lastLine)) lines.pop()

  const summaryLines: string[] = []
  const keyPoints: string[] = []
  const yourResponsibilities: string[] = []
  let section: (typeof SECTION_HEADINGS)[SectionHeading] = 'summary'

  for (const line of lines) {
    if (!line) continue
    const heading = line.replace(/:$/, '').toUpperCase()
    if (isHeading(heading)) {
      section = SECTION_HEADINGS[heading]
      continue
    }

    const item = line.replace(/^[-*•]\s*/, '')
    if (!item) continue
    if (section === 'keyPoints') keyPoints.push(item)
    else if (section === 'yourResponsibilities') yourResponsibilities.push(item)
    else summaryLines.push(item)
  }

  return { summary: summaryLines.join(' '), keyPoints, yourResponsibilities }
}

const SummaryPanel = ({
  icon: Icon,
  title,
  tone = 'default',
  children,
}: {
  icon: LucideIcon
  title: string
  tone?: 'default' | 'highlight'
  children: ReactNode
}) => (
  <div
    className={cn(
      'min-w-0 rounded-xl border p-4 sm:p-5',
      tone === 'highlight'
        ? 'border-rose-200 bg-rose-50 dark:border-rose-500/25 dark:bg-rose-500/10'
        : 'border-slate-200 bg-white dark:border-white/10 dark:bg-white/[0.03]'
    )}
  >
    <div className='mb-3 flex items-center gap-2'>
      <span
        className={cn(
          'flex h-7 w-7 shrink-0 items-center justify-center rounded-md',
          tone === 'highlight'
            ? 'bg-rose-500 text-white'
            : 'bg-slate-100 text-slate-700 dark:bg-white/10 dark:text-slate-200'
        )}
      >
        <Icon className='h-4 w-4' />
      </span>
      <h3 className='text-sm font-semibold tracking-tight text-slate-900 dark:text-slate-50'>{title}</h3>
    </div>
    {children}
  </div>
)

const KeyPointsList = ({ items }: { items: string[] }) => (
  <ol className='space-y-3'>
    {items.map((item, index) => (
      <li key={`${index}-${item}`} className='flex gap-3'>
        <span className='mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-slate-900 text-[11px] font-semibold tabular-nums text-white dark:bg-slate-100 dark:text-slate-900'>
          {index + 1}
        </span>
        <span className='min-w-0 pt-0.5 text-sm leading-6 break-words text-slate-700 dark:text-slate-300'>{item}</span>
      </li>
    ))}
  </ol>
)

const ActionsList = ({ items }: { items: string[] }) => (
  <ul className='space-y-3'>
    {items.map((item, index) => (
      <li key={`${index}-${item}`} className='flex gap-3'>
        <span className='mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border-2 border-rose-500 bg-white text-rose-600 dark:bg-transparent dark:text-rose-400'>
          <Check className='h-3 w-3' strokeWidth={3} />
        </span>
        <span className='min-w-0 text-sm leading-6 break-words text-slate-800 dark:text-slate-200'>{item}</span>
      </li>
    ))}
  </ul>
)

export const PolicyAiSummaryCard = ({
  content,
  isStreaming,
  error,
  onClose,
  stacked = false,
  className,
  hideFooter = false,
}: {
  content: string
  isStreaming: boolean
  error: string | null
  onClose: () => void
  /** Keep key points and actions in one column, for narrow containers such as detail modals. */
  stacked?: boolean
  className?: string
  hideFooter?: boolean
}) => {
  const parsed = parsePolicySummary(content, isStreaming)
  const hasContent = !!(parsed.summary || parsed.keyPoints.length || parsed.yourResponsibilities.length)
  const hasBothLists = parsed.keyPoints.length > 0 && parsed.yourResponsibilities.length > 0
  const showError = !isStreaming && !!error

  return (
    <section
      aria-label='AI policy summary'
      aria-busy={isStreaming}
      className={cn(
        'mx-auto mb-6 overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 shadow-sm dark:border-white/10 dark:bg-slate-900/60',
        className
      )}
    >
      <header className='flex items-center justify-between gap-3 bg-gradient-to-r from-red-500 to-rose-500 px-4 py-3.5 text-white sm:px-5'>
        <div className='flex min-w-0 items-center gap-3'>
          <span className='flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/20 ring-1 ring-white/30'>
            <Sparkles className={cn('h-4 w-4', isStreaming && 'animate-pulse')} />
          </span>
          <div className='min-w-0'>
            <h2 className='text-base leading-tight tracking-tight'>AI summary</h2>
            <p className='mt-0.5 text-xs leading-snug text-white/85 sm:truncate'>
              {isStreaming
                ? 'Reading the policy and writing a summary...'
                : showError
                  ? 'The summary could not be generated'
                  : 'A quick overview to help you read this policy'}
            </p>
          </div>
        </div>
        <Button
          variant='ghost'
          size='icon'
          className='h-8 w-8 shrink-0 text-white hover:bg-white/15 hover:text-white'
          onClick={onClose}
          aria-label='Hide AI summary'
        >
          <X className='h-4 w-4' />
        </Button>
      </header>

      <div className='space-y-4 p-4 sm:p-5'>
        {isStreaming && !hasContent && (
          <div className='space-y-3 rounded-xl border border-slate-200 bg-white p-5 dark:border-white/10 dark:bg-white/[0.03]'>
            <Skeleton className='h-4 w-1/3' />
            <Skeleton className='h-3.5 w-full' />
            <Skeleton className='h-3.5 w-11/12' />
            <Skeleton className='h-3.5 w-2/3' />
          </div>
        )}

        {showError && (
          <div className='flex items-start gap-3 rounded-xl border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive'>
            <AlertCircle className='mt-0.5 h-4 w-4 shrink-0' />
            <p className='min-w-0 leading-6 break-words'>{error}</p>
          </div>
        )}

        {parsed.summary && (
          <SummaryPanel icon={FileText} title='Overview'>
            <p className='text-[15px] leading-7 break-words text-slate-700 dark:text-slate-300'>{parsed.summary}</p>
          </SummaryPanel>
        )}

        {(parsed.keyPoints.length > 0 || parsed.yourResponsibilities.length > 0) && (
          <div className={cn('grid grid-cols-1 gap-4', hasBothLists && !stacked && 'xl:grid-cols-2')}>
            {parsed.keyPoints.length > 0 && (
              <SummaryPanel icon={Lightbulb} title='Key points'>
                <KeyPointsList items={parsed.keyPoints} />
              </SummaryPanel>
            )}
            {parsed.yourResponsibilities.length > 0 && (
              <SummaryPanel icon={ClipboardCheck} title='What you need to do' tone='highlight'>
                <ActionsList items={parsed.yourResponsibilities} />
              </SummaryPanel>
            )}
          </div>
        )}

        {isStreaming && hasContent && (
          <p className='flex items-center gap-2 text-xs font-medium text-rose-600 dark:text-rose-400'>
            <span className='h-2 w-2 animate-pulse rounded-full bg-rose-500' aria-hidden />
            Still writing...
          </p>
        )}
      </div>

      {hasContent && !isStreaming && !hideFooter && (
        <footer className='flex items-start gap-2 border-t border-slate-200 bg-white/70 px-4 py-3 text-xs leading-5 text-slate-500 sm:px-5 dark:border-white/10 dark:bg-white/[0.02] dark:text-slate-400'>
          <Info className='mt-0.5 h-3.5 w-3.5 shrink-0' />
          <p>
            AI-generated to help you read. It does not replace the policy&nbsp;-&nbsp;
            <span className='font-medium text-slate-700 dark:text-slate-200'>
              read the full policy before acknowledging.
            </span>
          </p>
        </footer>
      )}
    </section>
  )
}
